// src/pages/earningsImport/rideHailingWeekPeriods.js
import { db } from "../../firebase/firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

export const RIDE_HAILING_WEEK_KEYS = ["week1", "week2", "week3", "week4", "week5"];

function pad(n) {
  return String(n).padStart(2, "0");
}

export function formatDateInput(value) {
  if (!value) return "";
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function makeDefaultRideHailingWeeks(month) {
  const [year, monthNo] = String(month || "").split("-").map(Number);
  if (!year || !monthNo) {
    return RIDE_HAILING_WEEK_KEYS.reduce((acc, key) => {
      acc[key] = { startDate: "", endDate: "" };
      return acc;
    }, {});
  }

  const lastDay = new Date(year, monthNo, 0).getDate();
  const starts = [1, 8, 15, 22, 29];

  return RIDE_HAILING_WEEK_KEYS.reduce((acc, key, idx) => {
    const startDay = starts[idx];
    const endDay = Math.min(startDay + 6, lastDay);
    acc[key] = {
      startDate: `${year}-${pad(monthNo)}-${pad(startDay)}`,
      endDate: `${year}-${pad(monthNo)}-${pad(endDay)}`,
    };
    return acc;
  }, {});
}

function normalizeWeekRow(row = {}) {
  return {
    startDate: formatDateInput(row.startDate || row.fromDate || row.start || ""),
    endDate: formatDateInput(row.endDate || row.toDate || row.end || ""),
  };
}

export async function loadRideHailingWeekPeriods(month) {
  const defaults = makeDefaultRideHailingWeeks(month);
  if (!month) return { month, weeks: defaults, isDefault: true };

  const ref = doc(db, "rideHailingWeekPeriods", month);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    return { month, weeks: defaults, isDefault: true };
  }

  const data = snap.data() || {};
  const savedWeeks = data.weeks || {};
  const weeks = { ...defaults };

  RIDE_HAILING_WEEK_KEYS.forEach((key) => {
    const saved = normalizeWeekRow(savedWeeks[key] || {});
    weeks[key] = {
      startDate: saved.startDate || defaults[key].startDate,
      endDate: saved.endDate || defaults[key].endDate,
    };
  });

  return { month, weeks, isDefault: false, ...data };
}

export async function saveRideHailingWeekPeriods(month, weeks, userEmail = "") {
  const cleanWeeks = {};
  RIDE_HAILING_WEEK_KEYS.forEach((key) => {
    cleanWeeks[key] = normalizeWeekRow(weeks[key] || {});
  });

  await setDoc(
    doc(db, "rideHailingWeekPeriods", month),
    {
      month,
      weeks: cleanWeeks,
      updatedAt: serverTimestamp(),
      updatedBy: userEmail || "",
    },
    { merge: true }
  );
}

export function getConfiguredWeekRange(config, weekKey, fallbackMonth) {
  const key = String(weekKey || "").toLowerCase();
  const fallbackWeeks = makeDefaultRideHailingWeeks(fallbackMonth);
  const row = (config?.weeks && config.weeks[key]) || fallbackWeeks[key];
  if (!row?.startDate || !row?.endDate) return null;

  const start = new Date(`${row.startDate}T00:00:00`);
  const end = new Date(`${row.endDate}T23:59:59`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  if (end < start) return null;

  return { start, end, startDate: row.startDate, endDate: row.endDate };
}
